/**
 * MarketplaceAPI.js
 * Centralized API service for all marketplace-related API calls
 */

import fetchWithTimeout from '../../utils/fetchWithTimeout';
import getApiBaseUrl from '../../utils/apiBaseUrl';
import readJsonResponse from '../../utils/readJsonResponse';

const MARKETPLACE_API_FETCH_TIMEOUT_MS = 15000;
const MARKETPLACE_TRACKING_FETCH_TIMEOUT_MS = 5000;

const publicJsonHeaders = {
  'Accept': 'application/json',
  'Content-Type': 'application/json'
};

const marketplaceApiUrl = (path) => `${getApiBaseUrl()}${path}`;

/**
 * Helper to get auth headers
 * Check both customAuthToken (for custom JWT) and Auth0 token
 */
const getAuthHeaders = (explicitToken = null) => {
  // Try custom token first, then Auth0 token
  const customToken = localStorage.getItem('customAuthToken');
  const auth0Token = localStorage.getItem('auth0Token');
  const token = explicitToken || customToken || auth0Token;

  return token
    ? { ...publicJsonHeaders, 'Authorization': `Bearer ${token}` }
    : { ...publicJsonHeaders };
};

/**
 * Track user actions (views, saves, deploys, etc.)
 */
export const trackAction = async (listingId, action) => {
  try {
    await fetchWithTimeout(marketplaceApiUrl(`/marketplace/listings/${listingId}/track`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action })
    }, MARKETPLACE_TRACKING_FETCH_TIMEOUT_MS);
  } catch (error) {
    console.error(`Failed to track ${action}:`, error);
  }
};

/**
 * Toggle strategy public/private status using existing Strategy endpoint
 */
export const toggleStrategyPublic = async (strategyId, isPublic) => {
  const response = await fetchWithTimeout(marketplaceApiUrl(`/strategies/${strategyId}`), {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ isPublic })
  }, MARKETPLACE_API_FETCH_TIMEOUT_MS);

  return readJsonResponse(response, { errorMessage: 'Failed to update strategy visibility' });
};

/**
 * Fork/clone a strategy using the ai-bot-builder clone endpoint
 * This is the single source of truth for cloning strategies
 * @param {string} strategyId - The strategy ID to clone
 * @param {string} aiStrategyId - Optional AI strategy ID for cloning revisions
 * @returns {Promise} Clone response with new strategy IDs
 */
export const forkStrategy = async (strategyId, aiStrategyId = null) => {
  const response = await fetchWithTimeout(marketplaceApiUrl('/ai-bot-builder/clone-strategy'), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      strategyId: strategyId,
      aiStrategyId: aiStrategyId
    })
  }, MARKETPLACE_API_FETCH_TIMEOUT_MS);

  return readJsonResponse(response, { errorMessage: 'Failed to clone strategy' });
};

/**
 * Generate a share link for a marketplace listing
 * Note: Marketplace items are always public - no password protection needed
 */
export const generateShareLink = async (listingId) => {
  const response = await fetchWithTimeout(marketplaceApiUrl(`/marketplace/listings/${listingId}/share`), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({})
  }, MARKETPLACE_API_FETCH_TIMEOUT_MS);

  return readJsonResponse(response, { errorMessage: 'Failed to generate share link' });
};


/**
 * Publish a strategy to marketplace
 */
export const publishStrategy = async (strategyId, options = {}, token = null) => {
  const response = await fetchWithTimeout(marketplaceApiUrl('/marketplace/listings/publish'), {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ strategyId, ...options })
  }, MARKETPLACE_API_FETCH_TIMEOUT_MS);

  return readJsonResponse(response, { errorMessage: 'Failed to publish strategy' });
};

/**
 * Get marketplace listings with filters
 */
export const getListings = async (filters = {}) => {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) {
      params.set(key, value);
    }
  });
  
  // Public browsing must not depend on auth/profile/payment state.
  const response = await fetchWithTimeout(marketplaceApiUrl(`/marketplace/listings?${params.toString()}`), {
    headers: publicJsonHeaders
  }, MARKETPLACE_API_FETCH_TIMEOUT_MS);

  return readJsonResponse(response, { errorMessage: 'Failed to fetch marketplace listings' });
};

/**
 * Get listing details
 * @param {string} token - Optional auth token (uses localStorage fallback if not provided)
 */
export const getListingDetail = async (listingId, token = null) => {
  // Public by default. Owner/settings flows can pass an explicit token.
  const headers = token
    ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    : publicJsonHeaders;
  const response = await fetchWithTimeout(marketplaceApiUrl(`/marketplace/listings/${listingId}`), {
    headers
  }, MARKETPLACE_API_FETCH_TIMEOUT_MS);

  return readJsonResponse(response, { errorMessage: 'Failed to fetch listing details' });
};

/**
 * Check if current user has purchased a specific listing
 * Returns { purchased: boolean, purchase: { status, createdAt } | null }
 */
export const getPurchaseStatus = async (listingId) => {
  const response = await fetchWithTimeout(marketplaceApiUrl(`/marketplace/listings/${listingId}/purchase-status`), {
    headers: getAuthHeaders(),
  }, MARKETPLACE_API_FETCH_TIMEOUT_MS);
  return readJsonResponse(response, { errorMessage: 'Failed to check purchase status' });
};

/**
 * Get strategy description (returns infographicUrl, description, assetClasses)
 * GET /marketplace/strategy/:id/description
 * Public endpoint — works without auth.
 */
export const getStrategyDescription = async (listingId) => {
  const response = await fetchWithTimeout(marketplaceApiUrl(`/marketplace/strategy/${listingId}/description`), {
    headers: publicJsonHeaders,
  }, MARKETPLACE_API_FETCH_TIMEOUT_MS);
  return readJsonResponse(response, { errorMessage: 'Failed to fetch strategy description' });
};

/**
 * Update listing settings (pricing, code protection)
 * PATCH /marketplace/listings/:id/settings
 * @param {string} token - Optional auth token (uses localStorage fallback if not provided)
 */
export const updateListingSettings = async (listingId, settings, token = null) => {
  const headers = token
    ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    : getAuthHeaders();
  const response = await fetchWithTimeout(marketplaceApiUrl(`/marketplace/listings/${listingId}/settings`), {
    method: 'PATCH',
    headers,
    body: JSON.stringify(settings),
  }, MARKETPLACE_API_FETCH_TIMEOUT_MS);
  return readJsonResponse(response, { errorMessage: 'Failed to update listing settings' });
};

/**
 * Upload a manual marketplace hero image.
 * POST /marketplace/listings/:id/hero-image
 */
export const uploadHeroImage = async (listingId, file, token = null) => {
  const formData = new FormData();
  formData.append('file', file);

  const customToken = localStorage.getItem('customAuthToken');
  const auth0Token = localStorage.getItem('auth0Token');
  const authToken = token || customToken || auth0Token;
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const response = await fetchWithTimeout(marketplaceApiUrl(`/marketplace/listings/${listingId}/hero-image`), {
    method: 'POST',
    headers,
    body: formData,
  }, MARKETPLACE_API_FETCH_TIMEOUT_MS);
  return readJsonResponse(response, { errorMessage: 'Failed to upload hero image' });
};

/**
 * Regenerate a marketplace presentation field (title, description, details, or image).
 * POST /marketplace/listings/:id/regenerate-:field
 * Owner-only, rate limited to 5/day per listing.
 * @param {string} listingId
 * @param {'title'|'description'|'details'|'image'} field
 * @param {string} token - Optional auth token
 */
export const regenerateField = async (listingId, field, token = null) => {
  const headers = token
    ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    : getAuthHeaders();
  const response = await fetchWithTimeout(marketplaceApiUrl(`/marketplace/listings/${listingId}/regenerate-${field}`), {
    method: 'POST',
    headers,
  }, MARKETPLACE_API_FETCH_TIMEOUT_MS);
  return readJsonResponse(response, { errorMessage: `Failed to regenerate ${field}` });
};

export const updateRuntimeSecretSetupNotes = async (revisionId, notes, token) => {
  const response = await fetchWithTimeout(
    marketplaceApiUrl(`/ai-bot-builder/revisions/${revisionId}/runtime-secret-setup-notes`),
    {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify({ notes }),
    },
    MARKETPLACE_API_FETCH_TIMEOUT_MS,
  );
  return readJsonResponse(response, { errorMessage: 'Failed to save setup instructions' });
};

export default {
  trackAction,
  toggleStrategyPublic,
  forkStrategy,
  generateShareLink,
  publishStrategy,
  getListings,
  getListingDetail,
  getPurchaseStatus,
  updateListingSettings,
  uploadHeroImage,
  regenerateField,
  updateRuntimeSecretSetupNotes,
};
